import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams, Link as RouterLink } from "react-router-dom";
import clsx from "clsx";

import { makeRequest } from "../../utils/request";
import { Button, CheckboxField, DateField, Dialog, DropdownField, EmptyState, InputField, Spinner, Text, View } from "../../components/base";
import { Icon } from "../../components/base/Icon";
import { FineTuningPipelineStepper } from "../../components/FineTuningPipelineStepper";
import { FineTuningMutationNotice, useMutationNotice } from "../../components/FineTuningMutationNotice";

// ---------------------------------------------------------------------------------------- types
// Mirrors the control plane's DatasetOut (and the recipe metadata / event shapes) that the
// /v1/fine_tuning/datasets endpoints return.

type DatasetSource = {
  agent_ids: string[];
  start: string;
  end: string;
  // origin is defaulted server-side ("production") and not settable from the create form;
  // still read here for the detail drawer, where legacy datasets may carry a non-default value.
  origin?: string;
};

type DatasetLlm = {
  base_url: string;
  model: string;
  // api_key_env is defaulted server-side ("OPENAI_API_KEY"); read here only for the detail drawer.
  api_key_env?: string;
};

type DatasetCounts = { train: number; val: number; test: number };

type DatasetRelabel = { cached: number; called: number; failed: number };

type DatasetFiles = { training: string | null; validation: string | null; test: string | null };

type DatasetEstimate = {
  sessions: number;
  direct_pairs: number;
  cache_hits: number;
  llm_calls_needed: number;
  projected_examples: number;
  projected_counts: DatasetCounts;
};

type DatasetStatus = "queued" | "building" | "ready" | "failed" | "cancelled";

type FineTuningDataset = {
  id: string;
  object: "dataset";
  name: string;
  status: DatasetStatus;
  recipe: string;
  seed: number;
  dry_run: boolean;
  max_sessions: number | null;
  max_examples: number | null;
  parent_dataset_id: string | null;
  source: DatasetSource | null;
  llm: DatasetLlm | null;
  params: Record<string, unknown>;
  counts: DatasetCounts | null;
  relabel: DatasetRelabel | null;
  files: DatasetFiles;
  estimate: DatasetEstimate | null;
  content_sha256: string | null;
  error: string | null;
  created_at: string;
  updated_at: string;
};

type DatasetEvent = {
  id: string;
  type: string;
  level: string;
  message: string;
  created_at: string;
};

// GET /datasets/recipes — the create dialog renders a form off params_schema/needs_* so a
// server-side recipe change needs zero UI edits; the list uses it for the transform-version stamp.
type DatasetRecipe = {
  name: string;
  description: string;
  needs_source: boolean;
  needs_llm: "required" | "optional" | "none";
  transform_version: string;
  params_schema: JsonSchema;
};

// ---------------------------------------------------------------------------------------- json-schema
// A recipe's params_schema is a pydantic model_json_schema; the create dialog renders one input per
// property purely from its shape. We only lean on the subset of JSON Schema pydantic emits.

type JsonSchemaNode = {
  type?: string | string[];
  default?: unknown;
  title?: string;
  description?: string;
  minimum?: number;
  maximum?: number;
  enum?: unknown[];
  items?: JsonSchemaNode;
  anyOf?: JsonSchemaNode[];
  allOf?: JsonSchemaNode[];
  $ref?: string;
};

type JsonSchema = JsonSchemaNode & {
  properties?: Record<string, JsonSchemaNode>;
  required?: string[];
  $defs?: Record<string, JsonSchemaNode>;
};

type ParamKind = "boolean" | "integer" | "number" | "enum" | "array" | "string";

type ParamField = {
  name: string;
  kind: ParamKind;
  required: boolean;
  description?: string;
  minimum?: number;
  maximum?: number;
  enumValues?: string[];
  itemKind?: "integer" | "number" | "string";
  default?: unknown;
};

// Follow a #/$defs/Name reference (pydantic emits enums/nested models into $defs).
const deref = (node: JsonSchemaNode, root: JsonSchema): JsonSchemaNode => {
  if (!node.$ref) return node;
  const key = node.$ref.split("/").pop();
  return (key && root.$defs?.[key]) || {};
};

// Optional[...] fields come through as anyOf/allOf (and $ref for enums); unwrap to the meaningful
// non-null subschema so we can pick an input for it.
const effectiveNode = (prop: JsonSchemaNode, root: JsonSchema): JsonSchemaNode => {
  if (prop.$ref) return deref(prop, root);
  const branch = prop.anyOf ?? prop.allOf;
  if (branch?.length) {
    const chosen = branch.find((b) => b.$ref || (b.type && b.type !== "null")) ?? branch[0];
    return deref(chosen, root);
  }
  return prop;
};

const pickType = (node: JsonSchemaNode): string | undefined => (Array.isArray(node.type) ? node.type.find((t) => t !== "null") : node.type);

// Normalise one property into the descriptor the form renders from.
const describeParam = (name: string, prop: JsonSchemaNode, root: JsonSchema, required: boolean): ParamField => {
  const eff = effectiveNode(prop, root);
  const description = prop.description ?? eff.description;
  const dflt = prop.default ?? eff.default;
  const enumRaw = eff.enum ?? prop.enum;
  const base = { name, required, description, default: dflt };

  if (enumRaw?.length) return { ...base, kind: "enum", enumValues: enumRaw.map(String) };
  const type = pickType(eff);
  if (type === "boolean") return { ...base, kind: "boolean" };
  if (type === "integer") return { ...base, kind: "integer", minimum: eff.minimum, maximum: eff.maximum };
  if (type === "number") return { ...base, kind: "number", minimum: eff.minimum, maximum: eff.maximum };
  if (type === "array") {
    const itemType = eff.items ? pickType(deref(eff.items, root)) : undefined;
    return { ...base, kind: "array", itemKind: itemType === "integer" || itemType === "number" ? itemType : "string" };
  }
  return { ...base, kind: "string" };
};

const recipeParamFields = (schema: JsonSchema): ParamField[] => {
  const required = new Set(schema.required ?? []);
  return Object.entries(schema.properties ?? {}).map(([name, prop]) => describeParam(name, prop, schema, required.has(name)));
};

// The form keeps every value as a string (or boolean for checkboxes) so partial/empty entry works;
// this seeds each input from the schema default.
const initialParamValue = (f: ParamField): string | boolean => {
  if (f.kind === "boolean") return f.default === true;
  if (f.kind === "array") return Array.isArray(f.default) ? f.default.join(", ") : "";
  return f.default != null ? String(f.default) : "";
};

// ready/failed/cancelled are settled; queued/building are still working (poll them).
const TERMINAL = new Set<DatasetStatus>(["ready", "failed", "cancelled"]);
const POLL_MS = 6000;

const DEFAULT_RECIPE = "inline_slot_v1";
// The composition recipe the "Compose synthetic…" action preselects (deriving a synthetic dataset
// from a ready parent). Gated behind it actually being offered by the control plane's recipe list.
const SYNTHETIC_RECIPE = "inline_slot_synthetic_v1";
// The control-plane contract default for an LLM-backed recipe; shown as a placeholder so an admin
// can leave base_url blank and get the same thing the CLI's `vr ft datasets create` sends. The
// API key env defaults to OPENAI_API_KEY server-side and is no longer surfaced in this form.
const DEFAULT_LLM_BASE_URL = "https://api.openai.com/v1";

const COLUMNS = "1.7fr 1.3fr 0.9fr 1.1fr 1fr";

// Timestamps from the control plane may be naive UTC (no offset); tag them UTC so the browser
// doesn't parse them as local time (which would skew every relative time).
const asUtc = (iso: string) => (/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);

const relativeTime = (iso: string | null): string => {
  if (!iso) return "—";
  const ms = Date.now() - new Date(asUtc(iso)).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const shortId = (id: string) => (id.length > 16 ? `${id.slice(0, 14)}…` : id);

const fmtCounts = (c: DatasetCounts | null): string => (c ? `${c.train} / ${c.val} / ${c.test}` : "—");

const fmtParam = (v: unknown): string => (v !== null && typeof v === "object" ? JSON.stringify(v) : String(v));

// A copy-pasteable `vr ft create` invocation prefilled with this dataset's file ids — there's no
// Jobs create UI (jobs are made with the CLI), so this is the "use in training" hand-off. Exposes
// all available file ids as shell vars (test is held out for evals) and wires train/val into the
// command; the caller only fills in a base model.
const trainingSnippet = (files: DatasetFiles): string => {
  const vars = [`TRAINING_FILE=${files.training}`];
  if (files.validation) vars.push(`VALIDATION_FILE=${files.validation}`);
  if (files.test) vars.push(`TEST_FILE=${files.test}`);
  const cmd = [`vr ft create --model <base-model> \\`, `  --training-file "$TRAINING_FILE"`];
  if (files.validation) {
    cmd[cmd.length - 1] += " \\";
    cmd.push(`  --validation-file "$VALIDATION_FILE"`);
  }
  return [...vars, "", ...cmd].join("\n");
};

// Same visual pill as FineTuningJobStatusPill, but with the dataset lifecycle's mapping:
// queued/building = in-progress (yellow), ready = success, failed = error, cancelled = neutral.
// (The Models page likewise carries its own local badge.)
const STATUS_LABELS: Record<DatasetStatus, string> = {
  queued: "Queued",
  building: "Building",
  ready: "Ready",
  failed: "Failed",
  cancelled: "Cancelled",
};

// Seeds for the create dialog. "New dataset" passes {}; "Compose synthetic…" passes the synthetic
// recipe + this dataset as the parent so the dialog opens ready to build a derived dataset.
type CreatePreset = { recipe?: string; parentDatasetId?: string };

const DatasetStatusBadge = ({ status }: { status: DatasetStatus }) => (
  <Text
    className={clsx(
      "w-auto px-2 py-0.5 rounded-full text-xs whitespace-nowrap",
      status === "ready" && "bg-success/10 dark:bg-success/20 text-success-dark dark:text-success-light",
      (status === "queued" || status === "building") && "bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300",
      status === "failed" && "bg-error/10 dark:bg-error/20 text-error-dark dark:text-error-light",
      status === "cancelled" && "bg-gray-100 dark:bg-gray-700/40 text-gray-700 dark:text-gray-300",
    )}
  >
    {STATUS_LABELS[status] ?? status}
  </Text>
);

export const AdminFineTuningDatasetsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("id");

  const [datasets, setDatasets] = useState<FineTuningDataset[]>([]);
  // recipe name -> transform_version, for the detail panel's transform stamp
  const [transforms, setTransforms] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // null = create dialog closed; an object (possibly empty) = open, seeded from the preset. "New
  // dataset" opens it with {}; "Compose synthetic…" opens it pre-set to the synthetic recipe + parent.
  const [createPreset, setCreatePreset] = useState<CreatePreset | null>(null);
  // page-level "toast" for the create flow (the detail panel keeps its own notice)
  const { notice, showSuccess, clearNotice } = useMutationNotice();

  const load = useCallback(async (showSpinner = false) => {
    if (showSpinner) setIsLoading(true);
    try {
      // recipes are static metadata; best-effort so a recipes hiccup never blanks the list
      const [datasetsRes, recipesRes] = await Promise.all([
        makeRequest<{ data: FineTuningDataset[] }>(`/v1/fine_tuning/datasets?limit=200`),
        makeRequest<{ data: DatasetRecipe[] }>(`/v1/fine_tuning/datasets/recipes`).catch(() => ({ data: [] as DatasetRecipe[] })),
      ]);
      setDatasets(datasetsRes.data);
      setTransforms(Object.fromEntries(recipesRes.data.map((r) => [r.name, r.transform_version])));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load datasets");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load(true);
  }, [load]);

  // Poll while any dataset is still queued/building so statuses and counts fill in live.
  const hasActive = datasets.some((d) => !TERMINAL.has(d.status));
  useEffect(() => {
    if (!hasActive) return;
    const t = setInterval(() => load(false), POLL_MS);
    return () => clearInterval(t);
  }, [hasActive, load]);

  const selected = datasets.find((d) => d.id === selectedId) || null;
  // Only offer "Compose synthetic…" when the control plane actually lists the synthetic recipe
  // (transforms is keyed by recipe name); otherwise the button would open a mislabeled dialog.
  const canCompose = SYNTHETIC_RECIPE in transforms;

  return (
    <View className="h-full w-full bg-gray-50 dark:bg-slate-900">
      <div className={clsx("h-full w-full relative transition-all duration-300", selectedId && "pr-[440px]")}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <Text className="text-xl font-semibold">Fine-Tuning Datasets</Text>
            <Text className="text-sm text-gray-500 dark:text-gray-400">
              {datasets.length} {datasets.length === 1 ? "dataset" : "datasets"}
            </Text>
            {hasActive && <Spinner className="!h-4 !w-4 !text-brand" />}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              type="button"
              onClick={() => {
                clearNotice();
                setCreatePreset({});
              }}
              title="Build a new dataset"
            >
              <Icon
                name="plus"
                size={16}
                className="text-brand"
              />
              <Text className="text-brand">New dataset</Text>
            </Button>
            <Button
              variant="ghost"
              type="button"
              onClick={() => load(true)}
            >
              <Icon
                name="refresh"
                size={18}
                className="text-brand"
              />
            </Button>
          </div>
        </div>

        <FineTuningPipelineStepper />

        <div
          className="grid border-b-2 border-gray-200 dark:border-gray-700 px-6"
          style={{ gridTemplateColumns: COLUMNS }}
        >
          {["Name", "Recipe", "Status", "Counts", "Created"].map((h) => (
            <span
              key={h}
              className="py-3 pr-2 text-sm font-semibold text-gray-700 dark:text-gray-200"
              title={h === "Counts" ? "train / val / test" : undefined}
            >
              {h}
            </span>
          ))}
        </div>

        <div
          className="overflow-y-auto"
          style={{ height: "calc(100% - 110px)" }}
        >
          {isLoading ? (
            <View className="flex items-center justify-center py-20">
              <Spinner className="!text-brand" />
            </View>
          ) : error ? (
            <View className="flex items-center justify-center py-20">
              <Text className="text-error">{error}</Text>
            </View>
          ) : datasets.length === 0 ? (
            <EmptyState
              icon="database"
              title="No datasets yet"
              description="Datasets built via `vr ft dataset` will appear here — they feed the training jobs."
            />
          ) : (
            datasets.map((ds) => (
              <div
                key={ds.id}
                onClick={() => setSearchParams({ id: ds.id })}
                className={clsx(
                  "grid items-center px-6 border-b border-gray-100 dark:border-gray-800 cursor-pointer",
                  "hover:bg-gray-100 dark:hover:bg-gray-800",
                  selectedId === ds.id && "bg-gray-100 dark:bg-gray-800",
                )}
                style={{ gridTemplateColumns: COLUMNS }}
              >
                <span
                  className="py-3 pr-2 flex items-center gap-1.5 truncate text-sm font-medium"
                  title={ds.name}
                >
                  <span className="truncate">{ds.name}</span>
                  {ds.dry_run && (
                    <span
                      className="flex-shrink-0 rounded bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 dark:text-gray-300"
                      title="Dry run — estimate only, no files produced"
                    >
                      dry run
                    </span>
                  )}
                </span>
                <span
                  className="py-3 pr-2 truncate text-sm text-gray-500 dark:text-gray-400"
                  title={ds.recipe}
                >
                  {ds.recipe}
                </span>
                <span className="py-3 pr-2">
                  <DatasetStatusBadge status={ds.status} />
                </span>
                <span
                  className="py-3 pr-2 truncate text-sm text-gray-500 dark:text-gray-400"
                  title="train / val / test"
                >
                  {fmtCounts(ds.counts)}
                </span>
                <span className="py-3 pr-2 truncate text-sm text-gray-500 dark:text-gray-400">{relativeTime(ds.created_at)}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {selected && (
        <DatasetDetailPanel
          summary={selected}
          transformVersion={transforms[selected.recipe] || null}
          canCompose={canCompose}
          onCompose={() => {
            clearNotice();
            setCreatePreset({ recipe: SYNTHETIC_RECIPE, parentDatasetId: selected.id });
          }}
          onClose={() => setSearchParams({})}
          onChanged={() => load(false)}
        />
      )}

      {/* mounted only while open so its form state resets per attempt */}
      {createPreset && (
        <NewDatasetDialog
          preset={createPreset}
          onClose={() => setCreatePreset(null)}
          onCreated={(ds) => {
            setCreatePreset(null);
            showSuccess(`Dataset "${ds.name}" queued${ds.dry_run ? " (estimate only)" : ""}.`);
            load(false);
            setSearchParams({ id: ds.id }); // select the new row
          }}
        />
      )}

      {/* create "toast" — floats over the list, clear of the right-hand detail panel */}
      {notice && (
        <FineTuningMutationNotice
          notice={notice}
          className="fixed bottom-6 left-6 z-30 max-w-sm shadow-lg"
        />
      )}
    </View>
  );
};

type DatasetDetailPanelProps = {
  summary: FineTuningDataset;
  transformVersion: string | null;
  canCompose: boolean;
  onCompose: () => void;
  onClose: () => void;
  onChanged: () => void;
};

const DatasetDetailPanel = ({ summary, transformVersion, canCompose, onCompose, onClose, onChanged }: DatasetDetailPanelProps) => {
  // the list row is already a full DatasetOut, but re-fetch the detail (and poll while active) so
  // counts/files/estimate stay fresh as the build progresses
  const [ds, setDs] = useState<FineTuningDataset>(summary);
  const [events, setEvents] = useState<DatasetEvent[]>([]);
  const [isCancelling, setIsCancelling] = useState(false);
  const { notice, showError, showSuccess, clearNotice } = useMutationNotice();
  const idRef = useRef(summary.id);
  idRef.current = summary.id;

  // a notice belongs to the dataset it was shown for — drop it when switching rows (keyed on the id:
  // the summary object itself gets a fresh identity on every list poll)
  useEffect(() => clearNotice(), [summary.id, clearNotice]);

  const isTerminal = TERMINAL.has(ds.status);
  // A ready, non-dry-run build has real training files — the point at which it can feed a training
  // job ("Use in training") or seed a synthetic composition ("Compose synthetic…").
  const isReadyWithFiles = ds.status === "ready" && !!ds.files.training;

  const load = useCallback(async () => {
    try {
      const [detail, eventsRes] = await Promise.all([
        makeRequest<FineTuningDataset>(`/v1/fine_tuning/datasets/${summary.id}`),
        makeRequest<{ data: DatasetEvent[] }>(`/v1/fine_tuning/datasets/${summary.id}/events`),
      ]);
      if (idRef.current !== summary.id) return; // stale after switching rows
      setDs(detail);
      setEvents(eventsRes.data);
    } catch {
      /* best-effort */
    }
  }, [summary.id]);

  useEffect(() => {
    setDs(summary);
    setEvents([]);
    load();
  }, [summary, load]);

  useEffect(() => {
    if (TERMINAL.has(ds.status)) return;
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [ds.status, load]);

  const onCancel = async () => {
    setIsCancelling(true);
    clearNotice();
    try {
      await makeRequest(`/v1/fine_tuning/datasets/${ds.id}`, { method: "DELETE" });
      showSuccess("Cancellation requested.");
      onChanged();
      load();
    } catch (e) {
      showError(e, "Failed to cancel the dataset");
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <div className="fixed right-0 top-0 h-screen w-[440px] bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 flex flex-col z-20">
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Text className="text-base font-semibold">Dataset</Text>
          <DatasetStatusBadge status={ds.status} />
        </div>
        <Button
          variant="ghost"
          type="button"
          onClick={onClose}
        >
          <Icon
            name="close"
            size={18}
            className="text-gray-500"
          />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        <Section
          label="Dataset ID"
          value={ds.id}
          mono
        />
        <Section
          label="Name"
          value={ds.name}
        />
        <Field
          label="Recipe"
          value={ds.recipe}
        />
        {transformVersion && (
          <Field
            label="Transform"
            value={transformVersion}
            mono
          />
        )}
        <Field
          label="Dry run"
          value={ds.dry_run ? "Yes" : "No"}
        />
        <Field
          label="Seed"
          value={String(ds.seed)}
        />
        {ds.max_sessions != null && (
          <Field
            label="Max sessions"
            value={String(ds.max_sessions)}
          />
        )}
        {ds.max_examples != null && (
          <Field
            label="Max examples"
            value={String(ds.max_examples)}
          />
        )}
        {ds.parent_dataset_id && (
          <div className="flex items-baseline justify-between gap-3">
            <Text className="text-xs uppercase text-gray-400 flex-shrink-0">Parent dataset</Text>
            <RouterLink
              to={`/admin/fine-tuning-datasets?id=${ds.parent_dataset_id}`}
              className="min-w-0 text-right"
              title="Open the parent dataset"
            >
              <Text className="break-all font-mono text-xs text-brand hover:underline">{ds.parent_dataset_id}</Text>
            </RouterLink>
          </div>
        )}
        <Field
          label="Created"
          value={new Date(asUtc(ds.created_at)).toLocaleString()}
        />
        <Field
          label="Updated"
          value={new Date(asUtc(ds.updated_at)).toLocaleString()}
        />

        {ds.source && (
          <div>
            <Text className="text-xs uppercase text-gray-400 mb-2">Source</Text>
            <div className="space-y-1">
              <Field
                label="Agents"
                value={ds.source.agent_ids.length ? ds.source.agent_ids.join(", ") : "—"}
                mono
              />
              <Field
                label="Window"
                value={`${new Date(asUtc(ds.source.start)).toLocaleString()} → ${new Date(asUtc(ds.source.end)).toLocaleString()}`}
              />
              {ds.source.origin && (
                <Field
                  label="Origin"
                  value={ds.source.origin}
                />
              )}
            </div>
          </div>
        )}

        {ds.llm && (
          <div>
            <Text className="text-xs uppercase text-gray-400 mb-2">LLM</Text>
            <div className="space-y-1">
              <Field
                label="Model"
                value={ds.llm.model}
              />
              {ds.llm.base_url && (
                <Field
                  label="Base URL"
                  value={ds.llm.base_url}
                  mono
                />
              )}
              {ds.llm.api_key_env && (
                <Field
                  label="API key env"
                  value={ds.llm.api_key_env}
                  mono
                />
              )}
            </div>
          </div>
        )}

        {Object.keys(ds.params || {}).length > 0 && (
          <div>
            <Text className="text-xs uppercase text-gray-400 mb-2">Params</Text>
            <div className="space-y-0.5">
              {Object.entries(ds.params).map(([k, v]) => (
                <div
                  key={k}
                  className="flex items-baseline justify-between gap-3"
                >
                  <Text className="font-mono text-xs text-gray-600 dark:text-gray-300 break-all flex-shrink-0">{k}</Text>
                  <Text className="text-sm text-right break-all">{fmtParam(v)}</Text>
                </div>
              ))}
            </div>
          </div>
        )}

        <Field
          label="Counts (train / val / test)"
          value={fmtCounts(ds.counts)}
        />

        {ds.relabel && (
          <div>
            <Text className="text-xs uppercase text-gray-400 mb-1">Relabel</Text>
            <div className="flex gap-4 text-sm">
              <span>
                <span className="text-gray-400">cached </span>
                {ds.relabel.cached}
              </span>
              <span>
                <span className="text-gray-400">called </span>
                {ds.relabel.called}
              </span>
              <span className={clsx(ds.relabel.failed > 0 && "text-error")}>
                <span className="text-gray-400">failed </span>
                {ds.relabel.failed}
              </span>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <Text className="text-xs uppercase text-gray-400">Files</Text>
          <FileRow
            label="Training"
            value={ds.files.training}
          />
          <FileRow
            label="Validation"
            value={ds.files.validation}
          />
          <FileRow
            label="Test"
            value={ds.files.test}
          />
        </div>

        {/* No Jobs create UI exists (jobs come from `vr ft create`), so surface a ready-to-run
            command prefilled with this dataset's file ids — the "use in training" affordance. */}
        {isReadyWithFiles && (
          <CopySnippet
            label="Use in training"
            snippet={trainingSnippet(ds.files)}
            hint="Fill in a base model, then run to start a fine-tuning job on these files."
          />
        )}

        {ds.content_sha256 && (
          <CopyableValue
            label="Content SHA-256"
            value={ds.content_sha256}
            display={`${ds.content_sha256.slice(0, 12)}…`}
          />
        )}

        {ds.estimate && (
          <div>
            <Text className="text-xs uppercase text-gray-400 mb-2">Estimate{ds.dry_run ? " (dry run)" : ""}</Text>
            <div className="space-y-1">
              <Field
                label="Sessions"
                value={String(ds.estimate.sessions)}
              />
              <Field
                label="Direct pairs"
                value={String(ds.estimate.direct_pairs)}
              />
              <Field
                label="Cache hits"
                value={String(ds.estimate.cache_hits)}
              />
              <Field
                label="LLM calls needed"
                value={String(ds.estimate.llm_calls_needed)}
              />
              <Field
                label="Projected examples"
                value={String(ds.estimate.projected_examples)}
              />
              <Field
                label="Projected (train / val / test)"
                value={fmtCounts(ds.estimate.projected_counts)}
              />
            </div>
          </div>
        )}

        {ds.error && (
          <div>
            <Text className="block text-xs uppercase text-gray-400 mb-1">Error</Text>
            <Text className="text-sm text-error whitespace-pre-wrap break-words">{ds.error}</Text>
          </div>
        )}

        <div>
          <Text className="text-xs uppercase text-gray-400 mb-2">Events</Text>
          {events.length === 0 ? (
            <Text className="text-sm text-gray-400">No events yet.</Text>
          ) : (
            <div className="space-y-2">
              {events.map((e) => (
                <div
                  key={e.id}
                  className="flex gap-2 text-sm"
                >
                  <span
                    className={clsx(
                      "mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0",
                      e.level === "error" ? "bg-error" : e.level === "warning" ? "bg-yellow-500" : "bg-info",
                    )}
                  />
                  <div className="min-w-0">
                    <Text className="block text-gray-800 dark:text-gray-200 break-words">{e.message}</Text>
                    <Text className="block text-xs text-gray-400 mt-0.5">{relativeTime(e.created_at)}</Text>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* outside the terminal guard so a success notice survives the status flipping terminal */}
      <FineTuningMutationNotice
        notice={notice}
        className="px-4 py-3"
      />

      {!isTerminal && (
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            className="w-full"
            variant="ghost"
            type="button"
            onClick={onCancel}
            isLoading={isCancelling}
          >
            <Text className="text-error">Cancel dataset</Text>
          </Button>
        </div>
      )}

      {isReadyWithFiles && canCompose && (
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            className="w-full"
            variant="ghost"
            type="button"
            onClick={onCompose}
            title="Build a synthetic dataset derived from this one"
          >
            <Icon
              name="plus"
              size={16}
              className="text-brand"
            />
            <Text className="text-brand">Compose synthetic…</Text>
          </Button>
        </div>
      )}
    </div>
  );
};

// makeRequest stringifies the proxied FastAPI {"detail": ...} into the Error message; recover it so
// a 422 renders as a readable line (validation errors come through as a list of {loc, msg}).
const formatCreateError = (e: unknown): string => {
  const msg = e instanceof Error && e.message ? e.message : "Failed to create the dataset.";
  if (!(msg.startsWith("{") || msg.startsWith("["))) return msg;
  const line = (d: unknown): string => {
    const o = d as { loc?: unknown[]; msg?: string };
    const loc = Array.isArray(o?.loc) ? o.loc.filter((p) => p !== "body").join(".") : "";
    const text = typeof o?.msg === "string" ? o.msg : JSON.stringify(d);
    return loc ? `${loc}: ${text}` : text;
  };
  try {
    const parsed = JSON.parse(msg);
    if (Array.isArray(parsed)) return parsed.map(line).join("\n");
    if (parsed && typeof parsed === "object") {
      const inner = (parsed as Record<string, unknown>).detail ?? (parsed as Record<string, unknown>).message ?? (parsed as Record<string, unknown>).msg;
      if (typeof inner === "string") return inner;
      if (Array.isArray(inner)) return inner.map(line).join("\n");
    }
  } catch {
    /* not structured JSON — fall through to the raw message */
  }
  return msg;
};

type NewDatasetDialogProps = {
  preset: CreatePreset;
  onClose: () => void;
  onCreated: (ds: FineTuningDataset) => void;
};

const NewDatasetDialog = ({ preset, onClose, onCreated }: NewDatasetDialogProps) => {
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [bootError, setBootError] = useState<string | null>(null);
  const [recipes, setRecipes] = useState<DatasetRecipe[]>([]);
  const [parents, setParents] = useState<FineTuningDataset[]>([]);

  // seeded from the preset so "Compose synthetic…" opens on the synthetic recipe with this dataset
  // as the parent; the bootstrap re-confirms the recipe once the recipe list is in.
  const [recipeName, setRecipeName] = useState(preset.recipe ?? DEFAULT_RECIPE);
  // generic
  const [name, setName] = useState("");
  const [seed, setSeed] = useState("42");
  const [dryRun, setDryRun] = useState(false);
  const [maxSessions, setMaxSessions] = useState("");
  const [maxExamples, setMaxExamples] = useState("");
  const [parentId, setParentId] = useState(preset.parentDatasetId ?? "");
  // source
  const [agentIds, setAgentIds] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  // llm
  const [model, setModel] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  // params (string, or boolean for checkboxes) keyed by property name
  const [paramValues, setParamValues] = useState<Record<string, string | boolean>>({});

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // recipes + the org's READY datasets (for the parent/composition picker) on open
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [recipesRes, parentsRes] = await Promise.all([
          makeRequest<{ data: DatasetRecipe[] }>(`/v1/fine_tuning/datasets/recipes`),
          makeRequest<{ data: FineTuningDataset[] }>(`/v1/fine_tuning/datasets?status=ready&limit=200`).catch(() => ({
            data: [] as FineTuningDataset[],
          })),
        ]);
        if (!alive) return;
        setRecipes(recipesRes.data);
        setParents(parentsRes.data);
        const has = (n?: string) => !!n && recipesRes.data.some((r) => r.name === n);
        // prefer the preset recipe, else the default, else whatever's first — but only if it exists
        setRecipeName(has(preset.recipe) ? preset.recipe! : has(DEFAULT_RECIPE) ? DEFAULT_RECIPE : (recipesRes.data[0]?.name ?? ""));
        setBootError(null);
      } catch (e) {
        if (alive) setBootError(e instanceof Error ? e.message : "Failed to load recipes");
      } finally {
        if (alive) setIsBootstrapping(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const recipe = recipes.find((r) => r.name === recipeName) || null;
  const paramFields = recipe ? recipeParamFields(recipe.params_schema) : [];

  // reseed the params form (and clear errors) whenever the selected recipe changes
  useEffect(() => {
    const r = recipes.find((x) => x.name === recipeName);
    const fields = r ? recipeParamFields(r.params_schema) : [];
    setParamValues(Object.fromEntries(fields.map((f) => [f.name, initialParamValue(f)])));
    setFieldErrors({});
    setFormError("");
  }, [recipeName, recipes]);

  const setParam = (n: string, v: string | boolean) => setParamValues((prev) => ({ ...prev, [n]: v }));

  const handleSave = async () => {
    if (!recipe) return;
    const errs: Record<string, string> = {};

    const trimmedName = name.trim();
    if (!trimmedName) errs.name = "Name is required.";

    let source: DatasetSource | null = null;
    if (recipe.needs_source) {
      const agents = agentIds
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      if (!agents.length) errs.agentIds = "At least one agent id is required.";
      if (!start) errs.start = "Start date is required.";
      if (!end) errs.end = "End date is required.";
      if (start && end && start > end) errs.end = "End must be on or after start.";
      // day-granularity inputs -> the ISO window the CLI/control plane expect (end-of-day exclusive-ish)
      // origin defaults to "production" server-side; not settable from this form.
      source = {
        agent_ids: agents,
        start: start ? new Date(`${start}T00:00:00`).toISOString() : "",
        end: end ? new Date(`${end}T23:59:59.999`).toISOString() : "",
      };
    }

    let llm: DatasetLlm | null = null;
    if (recipe.needs_llm !== "none") {
      const trimmedModel = model.trim();
      if (recipe.needs_llm === "required" && !trimmedModel) errs.model = "Model is required for this recipe.";
      // only attach llm when a model is set; optional recipes may omit it entirely
      if (trimmedModel) {
        llm = {
          base_url: baseUrl.trim() || DEFAULT_LLM_BASE_URL,
          model: trimmedModel,
        };
      }
    }

    // build (and required-check) params straight off the recipe's schema
    const params: Record<string, unknown> = {};
    for (const f of paramFields) {
      const raw = paramValues[f.name];
      if (f.kind === "boolean") {
        params[f.name] = raw === true;
        continue;
      }
      const s = typeof raw === "string" ? raw.trim() : "";
      if (f.kind === "array") {
        const tokens = s
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean);
        if (tokens.length) params[f.name] = f.itemKind === "string" ? tokens : tokens.map(Number);
        else if (f.required) errs[`param:${f.name}`] = "Required.";
        continue;
      }
      if (s === "") {
        if (f.required) errs[`param:${f.name}`] = "Required.";
        continue;
      }
      if (f.kind === "integer" || f.kind === "number") {
        const num = Number(s);
        if (Number.isNaN(num)) errs[`param:${f.name}`] = "Must be a number.";
        else params[f.name] = num;
      } else {
        params[f.name] = s; // string / enum
      }
    }

    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setFormError("");
      return;
    }
    setFieldErrors({});
    setFormError("");
    setIsSaving(true);
    try {
      const body: Record<string, unknown> = {
        name: trimmedName,
        recipe: recipe.name,
        seed: seed.trim() === "" ? 42 : Number(seed),
        dry_run: dryRun,
        max_sessions: maxSessions.trim() === "" ? null : Number(maxSessions),
        max_examples: maxExamples.trim() === "" ? null : Number(maxExamples),
        parent_dataset_id: parentId || null,
        source,
        llm,
        params,
      };
      const ds = await makeRequest<FineTuningDataset>(`/v1/fine_tuning/datasets`, { method: "POST", body });
      onCreated(ds);
    } catch (e) {
      // keep the dialog open and surface the 422 detail inline
      setFormError(formatCreateError(e));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      isOpen
      onClose={onClose}
      title="New dataset"
      size="lg"
      onSave={handleSave}
      saveLabel={dryRun ? "Estimate" : "Create dataset"}
      isSaving={isSaving}
    >
      {isBootstrapping ? (
        <View className="flex items-center justify-center py-16">
          <Spinner className="!text-brand" />
        </View>
      ) : bootError ? (
        <View className="p-6">
          <Text className="text-sm text-error break-words">{bootError}</Text>
        </View>
      ) : (
        <View className="p-4 flex flex-col gap-4 max-h-[70vh] overflow-y-auto">
          <DropdownField
            label="Recipe"
            value={recipeName}
            onChange={setRecipeName}
            options={recipes.map((r) => ({
              value: r.name,
              displayLabel: r.name,
              label: (
                <div className="flex flex-col">
                  <span className="font-medium">{r.name}</span>
                  {r.description && <span className="text-xs text-gray-500 dark:text-gray-400">{r.description}</span>}
                </div>
              ),
            }))}
          />
          {recipe?.description && <Text className="-mt-2 text-xs text-gray-500 dark:text-gray-400">{recipe.description}</Text>}

          <InputField
            label="Name *"
            placeholder="e.g. june-inline-slots"
            value={name}
            onChange={setName}
            error={fieldErrors.name}
          />

          <div className="grid grid-cols-2 gap-3">
            <InputField
              label="Seed"
              type="number"
              value={seed}
              onChange={setSeed}
              tooltip="Deterministic build seed (shuffling, splits)."
            />
            <DropdownField
              label="Parent dataset (optional)"
              value={parentId}
              onChange={setParentId}
              options={[{ label: "None", value: "" }, ...parents.map((p) => ({ value: p.id, label: `${p.name} · ${shortId(p.id)}`, displayLabel: p.name }))]}
              tooltip="Derive from an existing READY dataset instead of a fresh extraction."
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <InputField
              label="Max sessions (optional)"
              type="number"
              min={0}
              placeholder="no cap"
              value={maxSessions}
              onChange={setMaxSessions}
            />
            <InputField
              label="Max examples (optional)"
              type="number"
              min={0}
              placeholder="no cap"
              value={maxExamples}
              onChange={setMaxExamples}
            />
          </div>

          <CheckboxField
            label="Estimate only, no files/LLM cost"
            value={dryRun}
            onChange={setDryRun}
            inline
            size="sm"
          />

          {recipe?.needs_source && (
            <div className="flex flex-col gap-3 border-t border-gray-200 dark:border-gray-700 pt-4">
              <Text className="text-xs uppercase text-gray-400">Source</Text>
              <InputField
                label="Agent IDs *"
                placeholder="agent_abc, agent_def"
                value={agentIds}
                onChange={setAgentIds}
                error={fieldErrors.agentIds}
                tooltip="One or more source agent ids, comma-separated."
              />
              <div className="grid grid-cols-2 gap-3">
                <DateField
                  label="From *"
                  value={start}
                  onChange={(e) => setStart(e.target.value)}
                  error={fieldErrors.start}
                />
                <DateField
                  label="To *"
                  value={end}
                  onChange={(e) => setEnd(e.target.value)}
                  error={fieldErrors.end}
                />
              </div>
            </div>
          )}

          {recipe && recipe.needs_llm !== "none" && (
            <div className="flex flex-col gap-3 border-t border-gray-200 dark:border-gray-700 pt-4">
              <Text className="text-xs uppercase text-gray-400">LLM {recipe.needs_llm === "optional" ? "(optional)" : ""}</Text>
              <InputField
                label={recipe.needs_llm === "required" ? "Model *" : "Model"}
                placeholder="e.g. gpt-5.4"
                value={model}
                onChange={setModel}
                error={fieldErrors.model}
              />
              <InputField
                label="Base URL (optional)"
                placeholder={DEFAULT_LLM_BASE_URL}
                value={baseUrl}
                onChange={setBaseUrl}
              />
            </div>
          )}

          {paramFields.length > 0 && (
            <div className="flex flex-col gap-3 border-t border-gray-200 dark:border-gray-700 pt-4">
              <Text className="text-xs uppercase text-gray-400">Parameters</Text>
              {paramFields.map((f) => (
                <ParamFieldInput
                  key={f.name}
                  field={f}
                  value={paramValues[f.name] ?? (f.kind === "boolean" ? false : "")}
                  error={fieldErrors[`param:${f.name}`]}
                  onChange={(v) => setParam(f.name, v)}
                />
              ))}
            </div>
          )}

          {formError && (
            <div className="rounded-md border border-error/40 bg-error/5 p-3">
              <Text className="block text-sm text-error whitespace-pre-wrap break-words">{formError}</Text>
            </div>
          )}
        </View>
      )}
    </Dialog>
  );
};

// One input rendered purely from a params_schema property — the reusability point: a new recipe
// field appears here with no code change.
type ParamFieldInputProps = {
  field: ParamField;
  value: string | boolean;
  error?: string;
  onChange: (value: string | boolean) => void;
};

const ParamFieldInput = ({ field, value, error, onChange }: ParamFieldInputProps) => {
  const label = `${field.name}${field.required ? " *" : ""}`;
  const helper = field.description ? <Text className="mt-1 block text-xs text-gray-400">{field.description}</Text> : null;

  if (field.kind === "boolean") {
    return (
      <div>
        <CheckboxField
          label={label}
          value={value === true}
          onChange={(v) => onChange(v)}
          error={error}
          inline
          size="sm"
        />
        {helper}
      </div>
    );
  }

  if (field.kind === "enum") {
    return (
      <div>
        <DropdownField
          label={label}
          value={String(value)}
          onChange={(v) => onChange(v)}
          error={error}
          options={[{ label: field.required ? "— select —" : "— none —", value: "" }, ...(field.enumValues ?? []).map((v) => ({ label: v, value: v }))]}
        />
        {helper}
      </div>
    );
  }

  if (field.kind === "integer" || field.kind === "number") {
    return (
      <div>
        <InputField
          label={label}
          type="number"
          value={String(value)}
          onChange={(v) => onChange(v)}
          error={error}
          min={field.minimum}
          max={field.maximum}
          step={field.kind === "integer" ? 1 : "any"}
        />
        {helper}
      </div>
    );
  }

  if (field.kind === "array") {
    return (
      <div>
        <InputField
          label={label}
          placeholder={field.itemKind === "string" ? "comma-separated" : "comma-separated numbers"}
          value={String(value)}
          onChange={(v) => onChange(v)}
          error={error}
        />
        {helper}
      </div>
    );
  }

  return (
    <div>
      <InputField
        label={label}
        value={String(value)}
        onChange={(v) => onChange(v)}
        error={error}
      />
      {helper}
    </div>
  );
};

// A file id with a copy affordance (the app copies via navigator.clipboard elsewhere too).
const CopyableValue = ({ label, value, display }: { label: string; value: string; display?: string }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = () => {
    const p = navigator.clipboard?.writeText(value);
    p?.then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      },
      () => {
        /* clipboard can be blocked in non-secure contexts */
      },
    );
  };
  return (
    <div>
      <Text className="block text-xs uppercase text-gray-400 mb-1">{label}</Text>
      <div className="flex items-center gap-2">
        <Text className="min-w-0 flex-1 break-all font-mono text-xs">{display ?? value}</Text>
        <Button
          variant="ghost"
          type="button"
          size="sm"
          onClick={onCopy}
          title="Copy"
        >
          <Icon
            name={copied ? "check" : "copy"}
            size={14}
            className={copied ? "text-success" : "text-gray-400"}
          />
        </Button>
      </div>
    </div>
  );
};

// A multi-line, ready-to-copy code block (same clipboard affordance as CopyableValue, same <pre>
// styling as the job hyperparameters block) with a single copy button for the whole snippet.
const CopySnippet = ({ label, snippet, hint }: { label: string; snippet: string; hint?: string }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = () => {
    const p = navigator.clipboard?.writeText(snippet);
    p?.then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      },
      () => {
        /* clipboard can be blocked in non-secure contexts */
      },
    );
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Text className="text-xs uppercase text-gray-400">{label}</Text>
        <Button
          variant="ghost"
          type="button"
          size="sm"
          onClick={onCopy}
          title="Copy snippet"
        >
          <Icon
            name={copied ? "check" : "copy"}
            size={14}
            className={copied ? "text-success" : "text-gray-400"}
          />
        </Button>
      </div>
      <pre className="text-xs bg-gray-50 dark:bg-gray-900 rounded-md p-3 overflow-x-auto whitespace-pre">{snippet}</pre>
      {hint && <Text className="mt-1 block text-xs text-gray-400">{hint}</Text>}
    </div>
  );
};

const FileRow = ({ label, value }: { label: string; value: string | null }) =>
  value ? (
    <CopyableValue
      label={label}
      value={value}
    />
  ) : (
    <div>
      <Text className="block text-xs uppercase text-gray-400 mb-1">{label}</Text>
      <Text className="block text-sm text-gray-400">—</Text>
    </div>
  );

const Field = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => (
  <div className="flex items-baseline justify-between gap-3">
    <Text className="text-xs uppercase text-gray-400 flex-shrink-0">{label}</Text>
    <Text className={clsx("text-sm text-right break-words", mono && "font-mono text-xs")}>{value}</Text>
  </div>
);

const Section = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => (
  <div>
    {/* the design-system Text is inline; `block` keeps the value on its own line under the label */}
    <Text className="block text-xs uppercase text-gray-400 mb-1">{label}</Text>
    <Text className={clsx("block text-sm break-all", mono && "font-mono text-xs")}>{value}</Text>
  </div>
);
